import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { partnerApi } from '../../lib/api';

interface CredentialCreateDialogProps {
  onClose: () => void;
}

export const CredentialCreateDialog = ({ onClose }: CredentialCreateDialogProps) => {
  const [value, setValue] = useState('');
  const [show, setShow] = useState(false);
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: () => partnerApi.createCredential({ credentialType: 'api_key', value }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-credentials'] });
      onClose();
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true">
      <div className="w-full max-w-md rounded-md bg-white p-6 shadow-lg">
        <h2 className="text-lg font-medium">New credential</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          The plaintext value is sent over HTTPS, encrypted server-side with the platform's encryption
          key, and never echoed back. Once created you reference this credential by ID on a tool's slot.
        </p>
        <label className="mt-4 block text-sm">
          <span className="block font-medium">Type</span>
          <select className="mt-1 rounded-md border px-3 py-2 text-sm" value="api_key" disabled>
            <option value="api_key">api_key</option>
          </select>
        </label>
        <label className="mt-4 block text-sm">
          <span className="block font-medium">Value (plaintext)</span>
          <div className="mt-1 flex gap-2">
            <input
              type={show ? 'text' : 'password'}
              value={value}
              onChange={e => setValue(e.target.value)}
              className="flex-1 rounded-md border px-3 py-2 font-mono text-sm"
              placeholder="paste API key or bearer token"
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShow(s => !s)}
              className="rounded-md border px-3 py-2 text-sm hover:bg-muted">
              {show ? 'Hide' : 'Show'}
            </button>
          </div>
        </label>
        {createMutation.error && (
          <p className="mt-3 text-sm text-destructive">{(createMutation.error as Error).message}</p>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border px-4 py-2 text-sm hover:bg-muted">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => createMutation.mutate()}
            disabled={!value.trim() || createMutation.isPending}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
            {createMutation.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};
